import { useQuery } from "@tanstack/react-query";
import { client } from "@/data/client";
import { formatBRL, cn } from "@/lib/utils";
import type { ProductPerformance as ProductType, ProductStatus } from "@/data/types";

const STATUS_LABEL: Record<ProductStatus, string> = {
  ativo: "Ativo",
  "alta-demanda": "Alta demanda",
  promocao: "Promoção",
  sazonal: "Sazonal",
  pausado: "Pausado",
};

const STATUS_CHIP: Record<ProductStatus, string> = {
  ativo: "bg-success/15 text-success",
  "alta-demanda": "bg-brand-amber/15 text-brand-amber",
  promocao: "bg-chart-2/15 text-chart-2",
  sazonal: "bg-chart-3/15 text-chart-3",
  pausado: "bg-text-muted/20 text-text-muted",
};

export function ProductPerformance({ period }: { period: { periodStart: string; periodEnd: string } }) {
  const q = useQuery({
    queryKey: ["products", period],
    queryFn: () => client.getProducts(period),
  });

  const products = q.data ?? [];
  const sorted = [...products].sort((a, b) => b.amountSold - a.amountSold);
  const totalUnits = products.reduce((a, p) => a + p.unitsSold, 0);
  const totalAmount = products.reduce((a, p) => a + p.amountSold, 0);

  return (
    <section className="rounded-lg border border-border-subtle bg-bg-card p-6 space-y-5">
      <div className="flex items-center justify-between">
        <div className="space-y-1">
          <h2 className="text-lg font-semibold tracking-tight text-text-primary">Produtos Vendidos</h2>
          <p className="text-[13px] text-text-muted">Mix de exames odontológicos · ordenado por faturamento</p>
        </div>
        <div className="flex items-center gap-4 text-xs text-text-muted">
          <span>
            <span className="font-semibold text-text-primary">{totalUnits}</span> unidades
          </span>
          <span>·</span>
          <span>
            <span className="font-semibold text-brand-amber">{formatBRL(totalAmount)}</span>
          </span>
        </div>
      </div>

      <div className="overflow-hidden rounded-md border border-border-subtle">
        <table className="w-full">
          <thead className="bg-bg-secondary">
            <tr className="text-[11px] font-medium uppercase tracking-[0.1em] text-text-muted">
              <th className="px-5 py-3 text-left">Produto</th>
              <th className="px-5 py-3 text-left">Categoria</th>
              <th className="px-5 py-3 text-right">Qtd. vendida</th>
              <th className="px-5 py-3 text-right">Ticket</th>
              <th className="px-5 py-3 text-right">Valor total</th>
              <th className="px-5 py-3 text-left">Situação</th>
            </tr>
          </thead>
          <tbody>
            {q.isLoading && (
              <tr>
                <td colSpan={6} className="px-5 py-8 text-center text-sm text-text-muted">
                  Carregando…
                </td>
              </tr>
            )}
            {sorted.map((p) => (
              <ProductRow key={p.productId} product={p} />
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function ProductRow({ product }: { product: ProductType }) {
  return (
    <tr className="border-t border-border-subtle text-sm transition hover:bg-bg-card-hover">
      <td className="px-5 py-3 font-medium text-text-primary">{product.name}</td>
      <td className="px-5 py-3 text-text-secondary">{product.category}</td>
      <td className="px-5 py-3 text-right font-medium text-text-primary">{product.unitsSold}</td>
      <td className="px-5 py-3 text-right text-text-secondary">{formatBRL(product.ticket)}</td>
      <td className="px-5 py-3 text-right font-semibold text-text-primary">{formatBRL(product.amountSold)}</td>
      <td className="px-5 py-3">
        <span
          className={cn(
            "inline-flex rounded-full px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wider",
            STATUS_CHIP[product.status]
          )}
        >
          {STATUS_LABEL[product.status]}
        </span>
      </td>
    </tr>
  );
}
